import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import IconAvatar from 'shared/components/vxl/avatar/IconAvatar/IconAvatar';
import Button from 'shared/components/vxl/Button/Button';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import theme from 'shared/styles/theme';

export interface TrackerCardNudgeProps {
  nudge?: {
    /**
     * nudgeTitleText to display bottom nudge heading
     */
    nudgeTitleText?: string;
    /**
     * To display body text of bottom nudge
     */
    nudgeBodyText?: string;
    /**
     * To display Icon of the budge on left side
     */
    nudgeIcon?: string;
    /**
     * To action icon of the nudge
     */
    nudgeChevron?: boolean;
    /**
     * Click handler for bottom nudge
     */
    nudgeOnPress?: () => void;
    /**
     * To display nudge button text
     */
    nudgeButtonText?: string;
  };
}

const TrackerCardNudge = ({
  nudge,
}: TrackerCardNudgeProps): React.ReactElement | null => {
  const [isNudgeAreaFocused, setIsNudgeAreaFocused] = useState(false);
  const [isNudgeAreaHovered, setIsNudgeAreaHovered] = useState(false);
  const [isNudgeAreaPressed, setIsNudgeAreaPressed] = useState(false);

  if (!nudge?.nudgeTitleText) {
    return null;
  }

  const hasNudgeOrChevron = nudge?.nudgeChevron || nudge?.nudgeButtonText;

  return (
    <View
      style={[
        styles.nudgeContainer,
        hasNudgeOrChevron && isNudgeAreaFocused ? styles.focus : null,
        hasNudgeOrChevron && isNudgeAreaHovered ? styles.hover : null,
        hasNudgeOrChevron && isNudgeAreaPressed ? styles.press : null,
      ]}
    >
      <Pressable
        style={styles.infoContainer}
        onPress={() => nudge?.nudgeOnPress?.()}
        onHoverIn={() => setIsNudgeAreaHovered(true)}
        onHoverOut={() => setIsNudgeAreaHovered(false)}
        onBlur={() => setIsNudgeAreaFocused(false)}
        onFocus={() => setIsNudgeAreaFocused(true)}
        onPressIn={() => setIsNudgeAreaPressed(true)}
        onPressOut={() => setIsNudgeAreaPressed(false)}
      >
        <IconAvatar
          variant="MD"
          iconKey={nudge?.nudgeIcon}
          backgroundColor={theme.color.background.primary}
        />
        <View style={styles.textContainer}>
          <BodySmBoldText numberOfLines={3}>
            {nudge?.nudgeTitleText}
          </BodySmBoldText>
          {nudge?.nudgeBodyText ? (
            <BodySmText numberOfLines={3}>{nudge.nudgeBodyText}</BodySmText>
          ) : null}
        </View>
        {nudge?.nudgeChevron && !nudge?.nudgeButtonText ? (
          <View>
            <Icon
              iconKey="CHEVRON_RIGHT"
              iconColor={theme.color.text.primary}
              iconAccessibilityLabel="nudge-chevron"
            />
          </View>
        ) : null}
      </Pressable>
      {!nudge?.nudgeChevron && nudge?.nudgeButtonText ? (
        <View style={styles.button}>
          <Button
            label={nudge.nudgeButtonText}
            type="SECONDARY"
            size="SMALL"
            onPress={() => nudge?.nudgeOnPress?.()}
          />
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  button: {
    borderRadius: 4,
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
  focus: {
    outlineColor: theme.color.interactive.focused.border,
  },
  hover: {
    backgroundColor: theme.color.interactive.tertiaryHovered,
  },
  infoContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    padding: theme.spacing.m,
  },
  nudgeContainer: {
    backgroundColor: theme.color.interactive.tertiary,
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    width: '100%',
  },
  press: {
    backgroundColor: theme.color.interactive.tertiaryPressed,
  },
  textContainer: {
    flex: 1,
    marginLeft: theme.spacing.m,
  },
});

export default React.memo(TrackerCardNudge);
